'use client';
import React from 'react';
import { Avatar, Box, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { HeartIcon, HeartIconActive, IsExpert, IsInfluencerIcon, IsStaff, Ellipse, PrivacyPostIcon } from '@/public/icon';
import { PostModel } from '@/app/models/posts/Posts';
import { useTranslation } from '@/app/hooks/useTranslation';
import UserActionsPermissions from '@/app/components/Posts/Post/components/UserActionsPermissions';
import { PostTypeConfig } from '@/app/constants/typedef';
import { useFormatTime } from '@/app/hooks/useFormatTime';
import UserModel from '@/app/models/user/UserModel';
import utilsService from '@/app/helpers/utilsService';
import CenteredBox from '@/app/base-ui/CenterBox';
import { useState, useEffect } from 'react';
import EditPostModal from '@/app/components/Posts/EditPost';
import { useSnackbar } from '@/app/contexts/snack-bar-provider';
import { useGlobalPostState, PostUpdateType } from '@/app/contexts/global-post-state-context';
import { actionFeedServices } from '@/app/service/actionFeedService';
import { logger } from '@/app/lib/logger';

interface PostGridCardProps {
    data: PostModel;
    onDelete?: () => void;
    onHiddenPost?: (id: string) => void;
    onFollow?: () => void;
    onLike?: () => void;
    isFollowing?: boolean;
    onReport?: () => void;
    onBlockUser?: () => void;
}

const PostGridCard: React.FC<PostGridCardProps> = ({ data, onDelete, onHiddenPost, onFollow, onLike, isFollowing, onReport, onBlockUser }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const router = useRouter();
    const time = useFormatTime(data.createdAt);
    const { addSnackbar } = useSnackbar();
    const globalPostState = useGlobalPostState();
    const [openEditModal, setOpenModalEdit] = useState(false);

    // Internal state for immediate UI feedback
    const [localIsPublic, setLocalIsPublic] = useState(data.isPublic);

    // Sync internal state with props if it changes externally
    useEffect(() => {
        setLocalIsPublic(data.isPublic);
    }, [data.isPublic]);

    const handleTogglePrivacy = async () => {
        try {
            const currentIsPublic = localIsPublic;
            const newIsPublic = !currentIsPublic;
            
            await actionFeedServices.updateStatusFeedForOnlyPosts({ 
                type: 'posts', 
                id: data.id, 
                data: { is_public: newIsPublic ? 1 : 0 } 
            });
            
            setLocalIsPublic(newIsPublic);
            
            addSnackbar({
                message: newIsPublic 
                    ? (t('post_made_public') || '공개 게시글로 전환되었습니다.') 
                    : (t('post_made_private') || '비공개 게시글로 전환되었습니다.'),
                severity: 'info',
                type: 'toast',
            });

            // Notify global state
            globalPostState.updatePostField(data.id, 'isPublic', newIsPublic);
        } catch (error) {
            const errorMessage = utilsService.getErrorMessage(error);
            addSnackbar({
                message: errorMessage || 'Some thing went wrong',
                severity: 'error',
                type: 'toast',
            });
            logger.error(error);
        }
    };

    const handleCardClick = () => {
        router.push(`/community/posts/${data.id}`, { scroll: false });
    };

    const renderUserIcon = (userData: UserModel | null) => {
        if (userData?.isStaff || userData?.isExpert) {
            return (
                <IsStaff width="14px" height="14px" />
            );
        }
        if (userData?.level?.current_level) {
             const levelConfig = userData?.level?.current_level;
             return (
                <Typography
                    component="span"
                    color="primary.main"
                    variant="h2_10Bold"
                    sx={{ lineHeight: 'normal', verticalAlign: 'top', fontSize: '9px', whiteSpace: 'nowrap' }}
                >
                    {levelConfig.name || levelConfig.name_en}
                </Typography>
            );
        }
        return null;
    };

    const mediaCount = data.media?.length || 0;
    const firstMedia = data.media?.[0]?.mediaUrl || '/images/image.png';

    const formatLikeCount = (count: number) => {
        if (count >= 1000) {
            return (count / 1000).toFixed(1) + 'K';
        }
        return count;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, width: '100%' }}>
            {/* Image Section */}
            <Box 
                onClick={handleCardClick}
                sx={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: 0,
                    paddingBottom: '115.15%', // 190 / 165 * 100
                    borderRadius: '24px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    backgroundColor: theme.palette.grayscale.gray02
                }}
            >
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                    <Image
                        src={firstMedia}
                        alt={utilsService.stripHtml(typeof data.caption === 'string' ? data.caption : data.caption?.caption)}
                        fill
                        draggable={false}
                        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        style={{
                            objectFit: 'cover',
                            color: 'transparent' // Hide alt text if broken
                        }}
                        sizes="(max-width: 600px) 50vw, 33vw"
                    />
                </Box>
                
                {/* Page Counter Overlay (Top Right) */}
                {mediaCount > 1 && (
                    <Box sx={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 12, 
                        backgroundColor: 'rgba(0, 0, 0, 0.4)', 
                        borderRadius: '12px', 
                        px: 1.5, 
                        py: 0.5,
                        zIndex: 1
                    }}>
                        <Typography variant="h2_10Medium" sx={{ color: 'white' }}>
                            1/{mediaCount}
                        </Typography>
                    </Box>
                )}

                {/* Like Count Overlay (Bottom Left) */}
                <Box 
                    onClick={(e) => {
                        e.stopPropagation();
                        onLike?.();
                    }}
                    sx={{ 
                        position: 'absolute', 
                        bottom: 10, 
                        left: 10, 
                        display: 'flex', 
                        alignItems: 'center', 
                        zIndex: 1,
                        cursor: 'pointer',
                        '&:hover': {
                            opacity: 0.8
                        },
                        gap: '5.13px'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20 }}>
                        {data.isLike ? (
                            <HeartIconActive width={20} height={20} />
                        ) : (
                            <HeartIcon width={20} height={20} color="white" />
                        )}
                    </Box>
                    <Typography 
                        sx={{ 
                            color: 'black', 
                            fontSize: '12px', 
                            fontWeight: 400,
                            ml: 0.5, 
                            textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff' 
                        }}
                    >
                        {formatLikeCount(data.likeCount)}
                    </Typography>
                </Box>
            </Box>

            {/* Info Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 0.5 }}>
                {/* Avatar */}
                <Avatar 
                    src={data.owner?.avatarUrl?.avatar_og_url || ''} 
                    sx={{ width: 30, height: 30, cursor: 'pointer' }}
                    onClick={() => router.push(`/mychannel/partner/${data.owner?.id}`)}
                />
                
                {/* Nickname and Time */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                        <Typography 
                            variant="h2_12Bold" 
                            noWrap 
                            sx={{ 
                                color: theme.palette.grayscale.gray07, 
                                cursor: 'pointer',
                                flexShrink: 1
                            }}
                            onClick={() => router.push(`/mychannel/partner/${data.owner?.id}`)}
                        >
                            {data.owner?.nickname || 'UNKNOWN'}
                        </Typography>
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                            {renderUserIcon(data.owner)}
                        </Box>
                    </Box>
                    <CenteredBox sx={{ justifyContent: 'start', flexWrap: 'wrap', lineHeight: '100%' }}>
                        <Typography variant="h2_10Regular" sx={{ color: theme.palette.grayscale.gray05 }}>
                            {time}
                        </Typography>
                        {!localIsPublic && (
                            <>
                                <Box sx={{ px: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Ellipse />
                                </Box>
                                <PrivacyPostIcon />
                            </>
                        )}
                    </CenteredBox>
                </Box>

                {/* Action Menu (3 dots) */}
                <Box sx={{ mr: -1 }}>
                    <UserActionsPermissions 
                        ownerId={data.owner?.id}
                        postId={data.id}
                        isPublic={localIsPublic}
                        type={PostTypeConfig.Posts}
                        onEdit={() => setOpenModalEdit(true)}
                        onDelete={onDelete}
                        onHiddenPost={() => onHiddenPost?.(data.id)}
                        onFollow={onFollow}
                        onReport={onReport}
                        onBlockUser={onBlockUser}
                        isFollowing={isFollowing}
                        onTogglePrivacy={handleTogglePrivacy}
                    />
                </Box>
            </Box>
            {openEditModal && (
                <EditPostModal
                    id={data.id}
                    type={PostTypeConfig.Posts}
                    onClose={() => setOpenModalEdit(false)}
                />
            )}
        </Box>
    );
};

export default PostGridCard;
